import { ENTITY_ICONS } from '@/lib/icons/entityIcons'
import Link from '@/routing/Link'
import { usePathname, useRouter, useSearchParams } from '@/routing/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  PenSquare,
  ArrowLeft,
  Users,
  User,
  MessageSquare,
  HelpCircle,
  Bug,
  Lightbulb,
  Settings,
  ChevronsUpDown,
  Shield,
  Sun,
  Moon,
  Palette,
  CreditCard,
  Shuffle,
  Loader2,
  PanelLeft,
  SlidersHorizontal,
  Workflow,
} from '@digital-twins/design-system/icons'
import { Badge } from '@/components/ui/badge'
import { cn, capitalizeFirst } from '@/lib/utils'
import { ROUTES, homeNewAskHref } from '@/routes/routes'
import { getUserInitials } from '@/lib/utils/avatar'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { useLogoNeedsInversion } from '@/lib/hooks/use-logo-needs-inversion'
import { Text } from '@/components/ui/Text'
import { isTrialLike } from '@/lib/billing/tier'
import { CreditWalletBadge } from '@/components/billing/CreditWalletBadge'
import { useProfile } from '@/lib/hooks'
import { useOwnAvatarColor } from '@/lib/hooks/use-own-avatar-color'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useTwinChatEnabled } from '@/hooks/useTwinChatEnabled'
import { useCreateStudyEnabled } from '@/hooks/useCreateStudyEnabled'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import { useFeedback } from '@/lib/feedback'
import { useAuth } from '@/lib/config/auth-provider'
import { useTheme } from '@/lib/theme-provider'
import { useBrand } from '@/lib/brand-provider'
import { FAIRGEN_TWINS_LOGO_URL, FAIRGEN_TWINS_LOGO_URL_ON_DARK, resolveLegacyFairgenLogoUrl } from '@/lib/theme/fairgen-default-brand'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  SIDEBAR_ICON_SIZE,
  SIDEBAR_COLLAPSED_NAV_ICON_HOVER,
  SIDEBAR_EXPANDED_NAV_ICON_HOVER,
} from './sidebar.constants'
import { SidebarThreadsSection } from './SidebarThreadsSection'
import { SidebarReferralRow } from '@/components/referral/SidebarReferralRow'
import { REFERRAL_UI_ENABLED } from '@/components/referral/constants'
import { useTranslation } from 'react-i18next'
import { UiLanguageMenuItems } from '@/components/auth/UiLanguageMenuItems'
import { NotificationCenter } from '@/components/notifications/NotificationCenter'

/** Matches the cubic-bezier used by the Framer Motion sidebar width animation in AppShell */
const SIDEBAR_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'

const sidebarNotificationButtonBaseClassName =
  'h-8 w-8 shrink-0 rounded-[4px] border border-border'

export function isNavActive(href: string, pathname: string, hasExactMatch: boolean): boolean {
  return pathname === href || (href !== '/' && !hasExactMatch && pathname?.startsWith(href))
}

/**
 * Controlled tooltip that suppresses open events while `enabled` is false,
 * preventing the "all tooltips flash at once" race when TooltipContent
 * batch-mounts after the sidebar collapse animation.
 */
export function SidebarTooltip({
  children,
  content,
  side = 'right',
  enabled,
}: {
  children: React.ReactNode
  content: string
  side?: 'right' | 'top' | 'bottom' | 'left'
  enabled: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Tooltip
      open={enabled ? isOpen : false}
      onOpenChange={(open) => {
        if (enabled) setIsOpen(open)
      }}
    >
      {children}
      <TooltipContent side={side}>{content}</TooltipContent>
    </Tooltip>
  )
}

function SidebarUserMenuContent({
  isPlatformAdmin,
  onNavigate,
  onLogout,
  onToggleTheme,
  onOpenFeedback,
  isDark,
  itemClassName,
  tier,
  upgradeLabel,
}: {
  isPlatformAdmin: boolean
  onNavigate: (path: string) => void
  onLogout: () => void
  onToggleTheme: () => void
  onOpenFeedback: () => void
  isDark: boolean
  itemClassName: string
  tier: string | null | undefined
  /** Set while the account is on a trial or locked free plan. */
  upgradeLabel: string | null
}) {
  const { t } = useTranslation('app')

  // Help links and feedback live here (they used to sit in the sidebar footer).
  const handleHelpLinkClick = (item: (typeof HELP_LINK_ITEMS)[number]) => {
    track(AnalyticsEvents.SUPPORT_SIDEBAR_CLICKED, {
      action: item.key,
      page_url: window.location.pathname,
    })
    window.open(item.url, '_blank')
  }

  const handleFeedbackClick = () => {
    track(AnalyticsEvents.SUPPORT_SIDEBAR_CLICKED, {
      action: 'submit_feedback',
      page_url: window.location.pathname,
    })
    onOpenFeedback()
  }

  return (
    <>
      <DropdownMenuItem
        onClick={() => onNavigate(ROUTES.settingsTab('billing'))}
        className={itemClassName}
        data-testid="sidebar-menu-billing"
      >
        <span className="min-w-0 flex-1 whitespace-nowrap">{t('nav.billing')}</span>
        <CreditWalletBadge tier={tier} />
      </DropdownMenuItem>
      {upgradeLabel && (
        <DropdownMenuItem
          onClick={() => onNavigate(ROUTES.billingPlans)}
          className={cn(itemClassName, 'text-primary')}
          data-testid="sidebar-upgrade-inline"
        >
          <span>{upgradeLabel}</span>
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator className="bg-sidebar-border/20 dark:bg-[hsl(var(--muted))]" />
      <DropdownMenuItem data-testid="sidebar-user-menu-settings" onClick={() => onNavigate('/settings')} className={itemClassName}>
        <Settings className="h-4 w-4" strokeWidth={1.5} />
        <span>{t('nav.settings')}</span>
      </DropdownMenuItem>
      <UiLanguageMenuItems itemClassName={itemClassName} />
      <DropdownMenuItem
        data-testid="sidebar-user-menu-theme-toggle"
        onClick={(e) => { e.preventDefault(); onToggleTheme() }}
        className={itemClassName}
      >
        {isDark ? <Sun className="h-4 w-4" strokeWidth={1.5} /> : <Moon className="h-4 w-4" strokeWidth={1.5} />}
        <span>{isDark ? t('nav.lightMode') : t('nav.darkMode')}</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-sidebar-border/20 dark:bg-[hsl(var(--muted))]" />
      {HELP_LINK_ITEMS.map((item) => (
        <DropdownMenuItem
          key={item.key}
          onClick={() => handleHelpLinkClick(item)}
          className={itemClassName}
          data-testid={`sidebar-menu-${item.key.replace('_', '-')}`}
        >
          <item.icon className="h-4 w-4" strokeWidth={1.5} />
          <span>{t(item.labelKey)}</span>
        </DropdownMenuItem>
      ))}
      <DropdownMenuItem onClick={handleFeedbackClick} className={itemClassName} data-testid="sidebar-menu-feedback">
        <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
        <span>{t('nav.submitFeedback')}</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-sidebar-border/20 dark:bg-[hsl(var(--muted))]" />
      <DropdownMenuItem data-testid="sidebar-user-menu-sign-out" onClick={onLogout} className={itemClassName}>
        <span>{t('nav.signOut')}</span>
      </DropdownMenuItem>
    </>
  )
}

const HELP_LINK_ITEMS = [
  {
    key: 'report_bug',
    labelKey: 'nav.submitBug' as const,
    icon: Bug,
    url: 'https://portal.usepylon.com/fairgen/forms/submit-a-bug',
  },
  {
    key: 'request_feature',
    labelKey: 'nav.requestFeature' as const,
    icon: Lightbulb,
    url: 'https://portal.usepylon.com/fairgen/forms/feature-request',
  },
] as const



interface SidebarProps {
  isCollapsed: boolean
  toggleSidebar: () => void
  guestMode?: boolean
  onGuestNavClick?: () => void
  mode?: 'normal' | 'minimal'
  /**
   * Called after a sidebar navigation is allowed to proceed. The mobile sheet
   * passes its own close handler; the desktop rail passes nothing, because an
   * icon+label rail must not collapse itself out from under the click.
   */
  onNavigate?: () => void
  /**
   * Called before any sidebar-initiated navigation leaves the current route.
   * Return `true` to claim the navigation (the host is showing a confirm
   * dialog and will navigate itself); the sidebar then neither navigates nor
   * collapses. Return `false` to let it proceed.
   */
  onNavigateAway?: (to: string) => boolean
}

export function Sidebar({
  isCollapsed,
  toggleSidebar,
  guestMode = false,
  onGuestNavClick,
  mode = 'normal',
  onNavigate,
  onNavigateAway,
}: SidebarProps) {
  const { t } = useTranslation('app')
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const isMinimal = mode === 'minimal'

  const { user: authUser, logout } = useAuth()
  const { data: profile, mutate: refreshProfile } = useProfile()
  const {
    canCustomizeAvatarColor,
    shuffleAvatar,
    shuffling,
    avatarAppearanceProps,
  } = useOwnAvatarColor(profile, refreshProfile)
  const { org } = useCurrentOrg()
  const { open: openFeedback } = useFeedback()
  const { brandSettings, brandLogoUrlOnDark, brandIconLogoUrl } = useBrand()
  const { resolvedTheme, toggleTheme } = useTheme()
  // structural only: drives Sun/Moon icon swap, "Light/Dark mode" label and the
  // wordmark image-filter.
  // All color choices here flow through semantic tokens (see globals.css).
  const isDark = resolvedTheme === 'dark'
  // When only a dark logo is uploaded we inspect it directly to decide whether
  // it needs inversion in light mode. The hook returns `true` for null input, which
  // is the safe default when the light logo is absent and inversion is unknown.
  const hasLightLogo = Boolean(brandSettings.brandLogoUrl)
  const hasDarkLogoOnly = !hasLightLogo && Boolean(brandLogoUrlOnDark)
  // The server measured each logo when it was saved; pass the flag that belongs
  // to whichever logo this branch is actually inspecting. The browser cannot
  // measure a saved logo itself (the images bucket sends no CORS header).
  const logoNeedsInversion = useLogoNeedsInversion(
    hasDarkLogoOnly ? brandLogoUrlOnDark : (brandLogoUrlOnDark ? null : brandSettings.brandLogoUrl),
    hasDarkLogoOnly
      ? brandSettings.brandLogoOnDarkNeedsInversion
      : brandSettings.brandLogoNeedsInversion,
  )

  // White-label brand swap: when the org has opted into white-label AND has
  // uploaded the matching asset, render that asset; otherwise fall back to the
  // Fairgen default.
  //
  // Logo resolution (mirrors the BrandTab preview logic):
  //   dark mode  → brandLogoUrlOnDark → brandLogoUrl
  //   light mode → brandLogoUrl → brandLogoUrlOnDark (fallback: only dark logo uploaded)
  //
  // When using the dark logo as a light-mode fallback we apply brightness(0) to
  // make it visible against the white sidebar (same inversion used in the preview).
  // Diff is byte-stable when `whiteLabel === false`.
  const whiteLabelEnabled = (authUser?.featureFlags as { whiteLabel?: boolean } | undefined)?.whiteLabel === true
  const effectiveWhiteLabel = whiteLabelEnabled
  /* Frontend review BLOCKER 1: every new Assistant Platform v1 surface must agree with the
     server-enforced `assistantPlatform` flag (default ON): so a flag-off
     user never sees the Workflows or Skills nav entries below, `useAssistantPlatformEnabled`
     already resolves an unauthenticated/not-yet-loaded user to `false`, so there is no
     flash-then-vanish: the entry simply never renders until the flag is confirmed on. */
  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  const twinChatEnabled = useTwinChatEnabled()
  const createStudyEnabled = useCreateStudyEnabled()
  const orgName = org?.name?.trim() || authUser?.companyName?.trim() || null
  const brandIconSrc = effectiveWhiteLabel
    ? brandIconLogoUrl
    : '/sidebar-brand-logo.svg'
  const orgInitial = orgName ? (Array.from(orgName.trim())[0]?.toUpperCase() ?? null) : null
  const resolvedWordmarkUrl = effectiveWhiteLabel
    ? resolveLegacyFairgenLogoUrl(
        isDark
          ? (brandLogoUrlOnDark || brandSettings.brandLogoUrl)
          : (brandSettings.brandLogoUrl || brandLogoUrlOnDark),
      )
    : null
  // Guard: if the resolved wordmark URL is the same image as the icon logo, suppress it
  // to avoid rendering the same image twice side-by-side. Fall back to org name text instead.
  const wordmarkIsDuplicateOfIcon =
    resolvedWordmarkUrl !== null &&
    brandIconLogoUrl !== null &&
    resolvedWordmarkUrl === brandIconLogoUrl
  const brandWordmarkSrc = wordmarkIsDuplicateOfIcon
    ? null
    : resolvedWordmarkUrl || (effectiveWhiteLabel ? null : (isDark ? FAIRGEN_TWINS_LOGO_URL_ON_DARK : FAIRGEN_TWINS_LOGO_URL))
  const isUsingOrgIcon = effectiveWhiteLabel && Boolean(brandIconLogoUrl)
  const isUsingOrgWordmark = effectiveWhiteLabel && brandWordmarkSrc !== null && brandWordmarkSrc !== FAIRGEN_TWINS_LOGO_URL_ON_DARK && brandWordmarkSrc !== FAIRGEN_TWINS_LOGO_URL
  // Default Fairgen lockup ships a dark and a light variant, so it never needs inversion.
  // Org wordmarks are assumed to be color-correct — except in two edge cases:
  //   1. Dark mode + only a light logo uploaded: invert it to show on dark sidebar bg.
  //   2. Light mode + only a dark logo uploaded: invert it to show on white sidebar bg.
  // In both cases `logoNeedsInversion` detects a transparent-bg single-color logo.
  const hasDarkLogo = Boolean(brandLogoUrlOnDark)
  const wordmarkNeedsInvert = isUsingOrgWordmark
    ? (isDark && !hasDarkLogo && logoNeedsInversion) || (!isDark && hasDarkLogoOnly && logoNeedsInversion)
    : false
  const brandIconAlt = effectiveWhiteLabel ? (orgName || t('nav.homeFallback')) : t('nav.brandLogoAlt')
  const brandWordmarkAlt = isUsingOrgWordmark ? (orgName || t('nav.homeFallback')) : t('nav.productName')
  const brandLinkLabel = effectiveWhiteLabel ? (orgName || t('nav.homeFallback')) : t('nav.brandLinkAria')
  const isLockedFree = authUser?.subscriptionTier === 'locked_free'
  const isTrial = isTrialLike(profile?.subscriptionTier)
  const showUpgradeInline = isTrial || isLockedFree
  const trialDaysLeft = isTrial && profile?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(profile.trialEndsAt).getTime() - Date.now()) / 86_400_000))
    : null
  // Shown as an item in the user menu (the footer no longer carries it).
  const upgradeLabel = !showUpgradeInline
    ? null
    : isTrial && trialDaysLeft !== null
      ? trialDaysLeft === 0 ? 'Trial ended · Upgrade' : `Upgrade · ${trialDaysLeft}d left`
      : 'Upgrade'
  const showCreateStudy = createStudyEnabled && !isLockedFree

  const isSettingsPage = pathname.startsWith('/settings')

  // The threads rail is the sidebar's only scrolling region, so it only exists
  // where there is room to read a title and a thread to read: not on the icon
  // rail, not under the settings nav, and not for a guest with no account.
  const showThreadsSection = !isCollapsed && !isSettingsPage && !guestMode

  const userName = profile?.name
    ? profile.name.split(' ').map((part: string) => capitalizeFirst(part)).join(' ')
    : t('nav.userFallback')
  const displayName = userName
  const userRole = profile?.orgRole ? capitalizeFirst(profile.orgRole) : ''
  const isPlatformAdmin = profile?.isPlatformAdmin === true
  const avatarInitials = useMemo(() => getUserInitials(profile?.name, profile?.email), [profile?.name, profile?.email])
  const [tooltipsReady, setTooltipsReady] = useState(isCollapsed)
  const tooltipsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setTooltipsReady(false)
    if (tooltipsTimerRef.current) clearTimeout(tooltipsTimerRef.current)
    // 600ms > 550ms sidebar transition — tooltips only appear after animation settles
    tooltipsTimerRef.current = setTimeout(() => setTooltipsReady(isCollapsed), 600)
    return () => {
      if (tooltipsTimerRef.current) clearTimeout(tooltipsTimerRef.current)
    }
  }, [isCollapsed])

  // Entries carry a `ROUTES` constant wherever one exists (CLAUDE.md); `/tests`,
  // `/chats` and `/audiences` have none yet and stay literal until they migrate.
  const mainNavItems = [
    // No Threads row in either state. The Recent rail's own header is the only
    // way into the lobby, so a nav entry would be a second control for the same
    // page (and would double-highlight, since `isNavActive` matches
    // `/threads/:token` by prefix while the rail marks the open row). It used to
    // survive on accounts with no threads yet; that made the row appear and then
    // vanish under the user the moment they started their first thread, which
    // read as a bug. An account with nothing in the rail now reaches `/threads`
    // from the composer or a direct link, not from here.
    // One flat action list: every row is icon + label in the same style, and
    // the order alone carries the hierarchy (no gradient CTA above it).
    // No Dashboard row: the brand link at the top is the way home.
    // `newAsk`: a link to `/` is a no-op when Home Lab is already open, so the
    // click handler pushes `homeNewAskHref()` to start a fresh Ask.
    ...(guestMode || showCreateStudy
      ? [{ href: homeNewAskHref(), label: t('nav.askAnything'), icon: PenSquare, slug: 'ask', newAsk: true }]
      : []),
    { href: ROUTES.tests, label: t('nav.runStudy'), icon: ENTITY_ICONS.study, slug: 'studies' },
    // Audiences has no row of its own: Conversations is where audiences live.
    ...(twinChatEnabled
      ? [{ href: ROUTES.chats, label: t('nav.talkToAudiences'), icon: ENTITY_ICONS.audience, slug: 'conversations' }]
      : []),
    { href: ROUTES.knowledge, label: t('nav.visitLibrary'), icon: ENTITY_ICONS.library, slug: 'library' },
    // Last, after Library. Join with Step 6.1 (assistant platform v1), which owns
    // `pages/WorkflowsPage/`, the `ROUTES.workflows` entry and the `AppRoutes.tsx`
    // registration line. Frontend review BLOCKER 1: gated on the flag, since
    // `/workflows` lists a synthetic fixture template client-side, which no
    // backend gate can hide.
    ...(assistantPlatformEnabled
      ? [{ href: ROUTES.workflows, label: t('nav.tracks'), icon: Workflow, slug: 'workflows' }]
      : []),
  ]

  const settingsNavItems = [
    { href: ROUTES.settingsTab('account'), label: t('nav.account'), icon: User, tab: 'account', slug: 'account' },
    // Join with Step 3.2: that step owns the Skills tab itself (`SettingsPageClient`
    // `allowed` list + `pageMeta` + the tab component). This is its nav entry.
    // Frontend review BLOCKER 1: gated on the flag, since the tab calls `GET /skills` on
    // mount and the backend 403s a flag-off org.
    ...(assistantPlatformEnabled
      ? [{ href: ROUTES.settingsTab('skills'), label: t('nav.llmSettings'), icon: SlidersHorizontal, tab: 'skills', slug: 'skills' }]
      : []),
    { href: ROUTES.settingsTab('billing'), label: t('nav.billing'), icon: CreditCard, tab: 'billing', slug: 'billing' },
    // { href: ROUTES.settingsTab('knowledge'), label: 'Knowledge', icon: ENTITY_ICONS.library, tab: 'knowledge' },
    { href: ROUTES.settingsTab('brand'), label: t('nav.brand'), icon: Palette, tab: 'brand', slug: 'brand' },
  ]

  const navItems = isSettingsPage ? settingsNavItems : mainNavItems
  const hasExactMatch = mainNavItems.some((item) => pathname === item.href)

  const activeSettingsTab = useMemo(() => {
    if (!isSettingsPage) return null
    if (pathname.startsWith('/settings/knowledge')) return 'knowledge'
    if (pathname.startsWith('/settings/billing')) return 'billing'
    return searchParams.get('tab') || 'account'
  }, [isSettingsPage, pathname, searchParams])

  const dropdownContentClass = cn(
    'w-56 bg-background',
    'border-[var(--sidebar-popover-border)]',
  )

  const dropdownItemClass = cn(
    'flex items-center gap-3 p-3 text-sm transition-colors',
    'text-sidebar-foreground/80 hover:text-sidebar-foreground focus:text-sidebar-foreground',
    'hover:bg-[var(--sidebar-popover-item-hover-bg)] focus:bg-[var(--sidebar-popover-item-hover-bg)]',
  )

  const handleLogout = async () => {
    await logout()
  }

  /**
   * True when the host claimed the navigation (e.g. unsaved study-creation work
   * pending a confirm dialog). Callers must not navigate or collapse the rail.
   */
  const isNavigationIntercepted = (to: string) => onNavigateAway?.(to) === true

  /** Programmatic sidebar navigation, routed through the host's exit guard. */
  const navigateGuarded = (to: string) => {
    if (isNavigationIntercepted(to)) return
    router.push(to)
  }

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <div className={cn(
        'flex h-full max-h-dvh w-full flex-col px-2 py-2 text-sidebar-foreground overflow-hidden',
        isCollapsed ? 'bg-transparent' : 'bg-background',
      )}>
        {/* ── Logo (conditional — different images & interactions) ── */}
        {/* Fixed height in both states (h-14 = 44px box + pb-3): the collapsed
            button and the expanded lockup share it, so toggling the rail does
            not shift the create button or the nav below it. */}
        <div className={cn('flex h-14 shrink-0 items-center pb-3', isCollapsed && 'justify-center')}>
          {isCollapsed ? (
            <SidebarTooltip content={t('nav.toggleSidebar')} enabled={tooltipsReady}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleSidebar}
                  aria-label={t('nav.toggleSidebar')}
                  aria-expanded={!isCollapsed}
                  data-testid={isMinimal ? 'sidebar-minimal-expand' : undefined}
                  className="group/logo relative flex h-11 w-11 items-center justify-center rounded-md transition-colors hover:bg-sidebar-foreground/5"
                >
                  {/* Minimal rail hides the nav entirely, so the panel glyph is the
                      permanent mark — no logo, no hover swap. Normal collapsed keeps
                      the brand icon and swaps to the expand chevron on hover. */}
                  {isMinimal ? (
                    <PanelLeft
                      className="h-[18px] w-[18px] text-sidebar-foreground/60 transition-colors group-hover/logo:text-sidebar-foreground"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <>
                      {brandIconSrc ? (
                        <img
                          src={brandIconSrc}
                          alt={brandIconAlt}
                          data-testid="sidebar-brand-logo"
                          className="h-8 w-[34px] object-contain transition-opacity duration-150 group-hover/logo:opacity-0"
                        />
                      ) : orgInitial ? (
                        <span
                          aria-label={brandIconAlt}
                          data-testid="sidebar-brand-logo"
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-current bg-transparent text-[13px] font-semibold text-sidebar-foreground transition-opacity duration-150 group-hover/logo:opacity-0"
                        >
                          {orgInitial}
                        </span>
                      ) : null}
                      <PanelLeft
                        className="absolute inset-0 m-auto h-[18px] w-[18px] text-sidebar-foreground/60 opacity-0 transition-opacity duration-150 group-hover/logo:opacity-100"
                        strokeWidth={1.5}
                      />
                    </>
                  )}
                </button>
              </TooltipTrigger>
            </SidebarTooltip>
          ) : (
            <div className="flex w-full items-center justify-between gap-3 rounded-md px-2">
              {/* Now the only way home on an expanded rail, so it says so on
                  hover — the brand alone does not read as navigation. */}
              <Link
                href="/"
                className="flex min-w-0 items-center gap-[11px]"
                aria-label={brandLinkLabel}
                title="Homepage"
                data-testid="sidebar-brand-home-link"
                onClick={(event) => {
                  if (isNavigationIntercepted('/')) event.preventDefault()
                }}
              >
                {effectiveWhiteLabel && !brandWordmarkSrc && orgInitial && (
                  <span
                    aria-hidden="true"
                    data-testid="sidebar-brand-logo"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-current bg-transparent text-[13px] font-semibold text-sidebar-foreground"
                  >
                    {orgInitial}
                  </span>
                )}
                <span className="flex min-w-0 flex-col justify-center">
                  {brandWordmarkSrc ? (
                    <img
                      src={brandWordmarkSrc}
                      alt={isUsingOrgWordmark ? brandWordmarkAlt : ''}
                      aria-hidden={isUsingOrgWordmark ? undefined : true}
                      data-testid="sidebar-brand-wordmark"
                      className={cn(
                        'shrink-0 object-contain',
                        isUsingOrgWordmark ? 'h-9 max-w-[160px]' : 'h-6 w-auto max-w-[120px]',
                        wordmarkNeedsInvert && 'brightness-0 invert',
                      )}
                    />
                  ) : effectiveWhiteLabel && orgName ? (
                    // No separate wordmark — icon was the only logo uploaded (or wordmark
                    // duplicates the icon). Render org name as text to avoid showing the
                    // same image twice.
                    <span
                      data-testid="sidebar-brand-wordmark"
                      className="text-[16px] font-semibold leading-none text-sidebar-foreground truncate max-w-[160px]"
                    >
                      {orgName}
                    </span>
                  ) : null}
                  {!effectiveWhiteLabel && (
                    <span className="text-[12px] font-medium leading-[1.4] text-[hsl(var(--sidebar-brand-caption))]">
                      {t('nav.earlyAccess')}
                    </span>
                  )}
                </span>
              </Link>

              <SidebarTooltip content={t('nav.toggleSidebar')} enabled={!isCollapsed}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    aria-label={t('nav.toggleSidebar')}
                    aria-expanded={!isCollapsed}
                    data-testid="sidebar-toggle"
                    className="hidden md:flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-sidebar-foreground/5"
                  >
                    <PanelLeft className="h-[18px] w-[18px] text-sidebar-foreground/70" strokeWidth={1.5} />
                  </button>
                </TooltipTrigger>
              </SidebarTooltip>
            </div>
          )}
        </div>

        {/* Minimal mode only strips the body while the rail is collapsed —
            expanding it hands back the full sidebar. */}
        {!(isMinimal && isCollapsed) && (
          <>
        {/* ── Back button (settings) ── */}
        {isSettingsPage ? (
          <div className="px-1">
            <SidebarTooltip content={t('nav.backToApp')} enabled={tooltipsReady}>
              <TooltipTrigger asChild>
                <Link
                  href="/"
                  data-testid="sidebar-back-button"
                  className={cn(
                    'flex items-center rounded-lg transition-colors',
                    'text-foreground/50 hover:text-primary',
                    isCollapsed
                      ? 'h-10 w-10 justify-center'
                      : 'w-full gap-2 px-3 py-2',
                  )}
                >
                  <ArrowLeft className="h-4 w-4 shrink-0" strokeWidth={2} />
                  <span
                    className={cn(
                      'overflow-hidden whitespace-nowrap text-[16px] font-medium transition-[max-width,opacity] duration-500',
                      isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100',
                    )}
                    style={{ transitionTimingFunction: SIDEBAR_EASE }}
                  >
                    {t('nav.backToApp')}
                  </span>
                </Link>
              </TooltipTrigger>
            </SidebarTooltip>
          </div>
        ) : null}

        {/* ── Navigation — icon always visible, label fades with the rail ── */}
        <nav
          className={cn(
            'flex flex-col py-2 transition-[gap,padding] duration-500',
            // With the threads rail mounted the nav is a fixed block and the
            // threads list is the single scrolling region. Without it (icon
            // rail, settings, guest) the nav keeps the scroll itself.
            showThreadsSection ? 'shrink-0' : 'min-h-0 flex-1 overflow-y-auto',
            isCollapsed ? 'items-center gap-1.5' : 'gap-1 px-1',
          )}
          style={{ transitionTimingFunction: SIDEBAR_EASE }}
        >
          {navItems.map((item) => {
            const isActive = isSettingsPage && 'tab' in item
              ? activeSettingsTab === item.tab
              : isNavActive(item.href, pathname, hasExactMatch)
            const isBeta = 'isBeta' in item && item.isBeta === true

            const navClassName = cn(
              'flex items-center overflow-hidden rounded-[4px] transition-all duration-500',
              isCollapsed
                ? cn(
                    'h-8 w-8 justify-center',
                    isActive
                      ? 'bg-[hsl(var(--sidebar-nav-active-bg))] text-[hsl(var(--sidebar-nav-active-fg))]'
                      : 'text-sidebar-foreground/72 hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground',
                  )
                : cn(
                    'min-h-8 px-2 py-2',
                    isActive
                      ? 'bg-[hsl(var(--sidebar-nav-active-bg))] text-[hsl(var(--sidebar-nav-active-fg))]'
                      : 'text-sidebar-foreground/82 hover:bg-sidebar-foreground/5 hover:text-sidebar-foreground',
                  ),
            )

            const navContent = (
              <>
                {/* Icon is permanent in both states — the expanded rail is
                    icon + label, not label alone. Only the label collapses,
                    which is what turns the expanded rail into an icon rail. */}
                <item.icon
                  style={{ width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }}
                  strokeWidth={1.5}
                  className="shrink-0"
                />
                <span
                  className={cn(
                    'flex min-w-0 items-center gap-1.5 overflow-hidden text-[14px] font-normal leading-5 transition-[max-width,opacity,margin] duration-500',
                    isCollapsed
                      ? 'ml-0 max-w-0 opacity-0'
                      : 'ml-2 max-w-[180px] opacity-100',
                  )}
                  style={{ transitionTimingFunction: SIDEBAR_EASE }}
                >
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {isBeta && <Badge variant="tag" className="shrink-0">Beta</Badge>}
                </span>
              </>
            )

            return (
              <SidebarTooltip
                key={item.href}
                content={isBeta ? `${item.label} · Beta` : item.label}
                enabled={tooltipsReady}
              >
                <TooltipTrigger asChild>
                  {guestMode ? (
                    <button
                      type="button"
                      onClick={onGuestNavClick}
                      data-testid={`sidebar-nav-${item.slug}`}
                      className={navClassName}
                      style={{ transitionTimingFunction: SIDEBAR_EASE }}
                    >
                      {navContent}
                    </button>
                  ) : (
                    <Link
                      href={item.href}
                      aria-current={isActive ? 'page' : undefined}
                      data-testid={`sidebar-nav-${item.slug}`}
                      className={navClassName}
                      style={{ transitionTimingFunction: SIDEBAR_EASE }}
                      onClick={(event) => {
                        if ('newAsk' in item) {
                          event.preventDefault()
                          if (isNavigationIntercepted(ROUTES.home)) return
                          onNavigate?.()
                          router.push(item.href)
                          return
                        }
                        if (isNavigationIntercepted(item.href)) {
                          event.preventDefault()
                          return
                        }
                        // Deliberately no collapse-on-click: the rail is a
                        // persistent icon+label shell. The mobile sheet closes
                        // itself through `onNavigate` instead.
                        onNavigate?.()
                      }}
                    >
                      {navContent}
                    </Link>
                  )}
                </TooltipTrigger>
              </SidebarTooltip>
            )
          })}
        </nav>

        {/* ── Live threads rail — the sidebar's only scrolling region ── */}
        {/* Unmount when Assistant is off: the section's own useSidebarThreads()
            fetches as soon as it mounts, and this is now the only gate on it. */}
        {showThreadsSection && assistantPlatformEnabled && (
          <SidebarThreadsSection
            pathname={pathname}
            onNavigate={onNavigate}
            onNavigateAway={onNavigateAway}
          />
        )}

        {/* ── Guest-mode bottom: theme toggle only ── */}
        {guestMode && (
          <div className={cn(
            'pt-2 pb-2',
            isCollapsed ? 'flex flex-col items-center' : 'flex flex-col px-3',
          )}>
            <button
              type="button"
              data-testid="sidebar-guest-theme-toggle"
              onClick={toggleTheme}
              className={cn(
                'flex items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors',
                'text-sidebar-foreground/80 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground',
              )}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              ) : (
                <Moon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              )}
              {!isCollapsed && <span>{isDark ? 'Light mode' : 'Dark mode'}</span>}
            </button>
          </div>
        )}

        {/* ── Bottom group: referral banner + user row (hidden in guest mode) ── */}
        {!guestMode && (
        <div className={cn(
          // shrink-0: the threads rail above is the flex-1 region, so the user
          // row must never give up height to it. mt-auto: belt and suspenders
          // for when that flex-1 region isn't in the tree at all (Assistant
          // off, or SidebarThreadsSection unmounting itself on zero threads) -
          // auto margins absorb any leftover space so this group still pins to
          // the bottom instead of riding up under short content.
          'mt-auto shrink-0 pt-2',
          isCollapsed ? 'flex flex-col items-center gap-1' : 'flex flex-col gap-1 px-1',
        )}>
          {REFERRAL_UI_ENABLED && !whiteLabelEnabled && <SidebarReferralRow isCollapsed={isCollapsed} />}

          {isCollapsed ? (
            <>
              <NotificationCenter
                className={cn(
                  sidebarNotificationButtonBaseClassName,
                  SIDEBAR_COLLAPSED_NAV_ICON_HOVER,
                )}
              />
              <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                <SidebarTooltip content={userName} enabled={tooltipsReady}>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:opacity-80"
                        title={userName}
                        aria-label={`${userName} menu`}
                        data-testid="sidebar-user-menu-trigger"
                      >
                        <UserAvatar
                          initials={avatarInitials}
                          name={userName}
                          size="xs"
                          {...avatarAppearanceProps}
                        />
                      </button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                </SidebarTooltip>
                <DropdownMenuContent align="start" side="right" className={dropdownContentClass}>
                  <SidebarUserMenuContent
                    isPlatformAdmin={isPlatformAdmin}
                    onNavigate={navigateGuarded}
                    onLogout={handleLogout}
                    onToggleTheme={toggleTheme}
                    onOpenFeedback={() => openFeedback({ triggerType: 'manual' })}
                    isDark={isDark}
                    itemClassName={dropdownItemClass}
                    tier={profile?.subscriptionTier}
                    upgradeLabel={upgradeLabel}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="flex w-full flex-col gap-1 px-2 py-1.5">
              <div className="group/avatar relative flex items-center gap-2">
                <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-2 transition-colors hover:bg-sidebar-foreground/6 rounded-md px-0.5 py-1"
                      title={userName}
                      aria-label={`${userName} menu`}
                      data-testid="sidebar-user-menu-trigger"
                    >
                      <UserAvatar
                        initials={avatarInitials}
                        name={userName}
                        size="xs"
                        {...avatarAppearanceProps}
                      />
                      <span className="min-w-0 flex-1 truncate text-left text-[14px] font-normal text-sidebar-foreground">
                        {displayName}
                        {profile?.orgRole?.toLowerCase() === 'guest' && (
                          <span className="ml-1 text-[11px] font-normal text-sidebar-foreground/50">· Guest</span>
                        )}
                      </span>
                      <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/45" strokeWidth={1.5} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="top" sideOffset={8} className={dropdownContentClass}>
                    <SidebarUserMenuContent
                      isPlatformAdmin={isPlatformAdmin}
                      onNavigate={navigateGuarded}
                      onLogout={handleLogout}
                      onToggleTheme={toggleTheme}
                      onOpenFeedback={() => openFeedback({ triggerType: 'manual' })}
                      isDark={isDark}
                      itemClassName={dropdownItemClass}
                      tier={profile?.subscriptionTier}
                      upgradeLabel={upgradeLabel}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
                {canCustomizeAvatarColor && (
                  <button
                    type="button"
                    onClick={shuffleAvatar}
                    aria-label={t('nav.changeAvatarColor')}
                    data-testid="sidebar-shuffle-avatar"
                    className="absolute left-0 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity duration-150 group-hover/avatar:opacity-100"
                  >
                    {shuffling ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-white" strokeWidth={1.5} />
                    ) : (
                      <Shuffle className="h-3.5 w-3.5 text-white" strokeWidth={1.5} />
                    )}
                  </button>
                )}
                <NotificationCenter
                  className={cn(
                    sidebarNotificationButtonBaseClassName,
                    SIDEBAR_EXPANDED_NAV_ICON_HOVER,
                  )}
                />
              </div>
            </div>
          )}
        </div>
        )}
          </>
        )}
      </div>
    </TooltipProvider>
  )
}
